import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import {
  ShippingLocation_,
  useShippingLocationsApi,
} from 'src/api/db/apiShippingLocations'
import {
  FormInput,
  FormSelect,
  FormWrapper,
} from 'src/shared/components/FormComponents'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/shared/components/ui/dialog'
import { useLocalization } from 'src/shared/hooks/useLocalization'

const formSchema = z.object({
  company_name: z.string().optional(),
  street_address: z.string().min(1, 'Street is required'),
  street_number: z.string().optional(),
  street_address_2: z.string().optional(),
  zip_code: z.string().min(1, 'Zip is required'),
  city: z.string().min(1, 'City is required'),
  country: z.string().min(1, 'Country is required'),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  phone_number: z.string().optional(),
  active: z.boolean().default(true),
  default: z.boolean().default(false),
})

const getCountries = (t: (en: string, sv?: string) => string) => [
  { value: 'SE', label: t('Sweden', 'Sverige') },
  { value: 'NO', label: t('Norway', 'Norge') },
  { value: 'DK', label: t('Denmark', 'Danmark') },
  { value: 'FI', label: t('Finland', 'Finland') },
  { value: 'GB', label: t('United Kingdom', 'Storbritannien') },
]

type FormValues = z.infer<typeof formSchema>

const emptyValues: FormValues = {
  company_name: '',
  street_address: '',
  street_number: '',
  street_address_2: '',
  zip_code: '',
  city: '',
  country: 'SE',
  first_name: '',
  last_name: '',
  phone_number: '',
  active: true,
  default: false,
}

/** Derive a display name for legacy list rows that still read `name`. */
const deriveLocationName = (data: FormValues) => {
  const company = data.company_name?.trim()
  if (company) return company
  const street = [data.street_address, data.street_number]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ')
  return street || null
}

export const AddEditShippingLocationDialog = ({
  isModalOpen,
  setIsModalOpen,
  editLocation,
  org_id,
  onShippingLocationCreated,
}: {
  isModalOpen: boolean
  setIsModalOpen: (open: boolean) => void
  editLocation?: ShippingLocation_
  org_id: string
  onShippingLocationCreated?: (locationId: number) => void
}) => {
  const { t } = useLocalization()
  const { createShippingLocation, updateShippingLocation } =
    useShippingLocationsApi({ org_id })
  const isEditMode = !!editLocation

  const methods = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: emptyValues,
  })

  useEffect(() => {
    if (!isModalOpen) return
    if (editLocation) {
      methods.reset({
        company_name: editLocation.company_name || '',
        street_address: editLocation.street_address || '',
        street_number: editLocation.street_number || '',
        street_address_2: editLocation.street_address_2 || '',
        zip_code: editLocation.zip_code || '',
        city: editLocation.city || '',
        country: editLocation.country || 'SE',
        first_name: editLocation.first_name || '',
        last_name: editLocation.last_name || '',
        phone_number: editLocation.phone_number || '',
        active: editLocation.active ?? true,
        default: editLocation.default ?? false,
      })
    } else {
      methods.reset(emptyValues)
    }
  }, [editLocation, isModalOpen, methods])

  const handleSubmit = async (data: FormValues) => {
    const payload = {
      name: deriveLocationName(data),
      street_address: data.street_address || null,
      street_address_2: data.street_address_2 || null,
      street_number: data.street_number || null,
      city: data.city || null,
      zip_code: data.zip_code || null,
      country: data.country || null,
      company_name: data.company_name || null,
      first_name: data.first_name || null,
      last_name: data.last_name || null,
      phone_number: data.phone_number || null,
      active: data.active,
      default: data.default,
    }

    try {
      if (isEditMode && editLocation) {
        await updateShippingLocation.mutateAsync({
          id: editLocation.id,
          ...payload,
        })
      } else {
        const { data: created, error } =
          await createShippingLocation.mutateAsync({
            org_id,
            ...payload,
          })
        if (error) throw error
        if (created?.id != null) {
          onShippingLocationCreated?.(created.id)
        }
      }
      setIsModalOpen(false)
    } catch (error) {
      console.error(
        `Failed to ${isEditMode ? 'update' : 'create'} shipping location:`,
        error
      )
    }
  }

  const isSaving =
    createShippingLocation.isPending || updateShippingLocation.isPending

  return (
    <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
      <DialogContent className="flex max-h-[min(90vh,720px)] w-full max-w-xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="shrink-0 border-b border-gray-100 px-6 py-5 pr-12">
          <DialogTitle>
            {isEditMode
              ? t('Edit shipping address', 'Redigera leveransadress')
              : t('Add shipping address', 'Lägg till leveransadress')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'Where should we deliver this order?',
              'Vart ska vi leverera den här ordern?'
            )}
          </DialogDescription>
        </DialogHeader>

        <FormWrapper
          methods={methods}
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col overflow-hidden space-y-0"
        >
          <Column className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            <Column className="gap-5">
              <Column className="gap-3">
                <h4>{t('Address', 'Adress')}</h4>
                <FormInput
                  label={t('Company', 'Företag')}
                  name="company_name"
                  methods={methods}
                  className="w-full"
                  placeholder={t('Company name', 'Företagsnamn')}
                />
                <Row className="gap-3">
                  <FormInput
                    label={t('Street', 'Gata')}
                    name="street_address"
                    methods={methods}
                    className="min-w-0 flex-1"
                    placeholder={t('Street name', 'Gatunamn')}
                  />
                  <FormInput
                    label={t('No.', 'Nr')}
                    name="street_number"
                    methods={methods}
                    className="w-24 shrink-0"
                    placeholder="12"
                  />
                </Row>
                <FormInput
                  label={t('Apt / floor (optional)', 'Lgh / våning (valfritt)')}
                  name="street_address_2"
                  methods={methods}
                  className="w-full"
                  placeholder={t('Apartment, suite…', 'Lägenhet, suite…')}
                />
                <Row className="gap-3">
                  <FormInput
                    label={t('Zip', 'Postnr')}
                    name="zip_code"
                    methods={methods}
                    className="w-32 shrink-0"
                    placeholder="111 22"
                  />
                  <FormInput
                    label={t('City', 'Stad')}
                    methods={methods}
                    name="city"
                    className="min-w-0 flex-1"
                    placeholder={t('City', 'Stad')}
                  />
                </Row>
                <FormSelect
                  label={t('Country', 'Land')}
                  name="country"
                  methods={methods}
                  options={getCountries(t)}
                  placeholder={t('Country', 'Land')}
                  className="w-full"
                />
              </Column>

              <Column className="gap-3 border-t border-gray-100 pt-5">
                <Column className="gap-1">
                  <h4>{t('Contact', 'Kontakt')}</h4>
                  <p className="text-sm text-muted-foreground">
                    {t(
                      'Who should we reach for delivery?',
                      'Vem ska vi kontakta vid leverans?'
                    )}
                  </p>
                </Column>
                <Row className="gap-3">
                  <FormInput
                    label={t('First name', 'Förnamn')}
                    name="first_name"
                    methods={methods}
                    className="min-w-0 flex-1"
                    placeholder={t('First name', 'Förnamn')}
                  />
                  <FormInput
                    label={t('Last name', 'Efternamn')}
                    name="last_name"
                    methods={methods}
                    className="min-w-0 flex-1"
                    placeholder={t('Last name', 'Efternamn')}
                  />
                </Row>
                <FormInput
                  label={t('Phone', 'Telefon')}
                  name="phone_number"
                  methods={methods}
                  className="w-full"
                  type="tel"
                  placeholder={t('Phone number', 'Telefonnummer')}
                />
              </Column>
            </Column>
          </Column>

          <DialogFooter className="shrink-0 border-t border-gray-100 px-6 py-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsModalOpen(false)}
            >
              {t('Cancel', 'Avbryt')}
            </Button>
            <Button type="submit" variant="primary" isLoading={isSaving}>
              {isEditMode
                ? t('Save address', 'Spara adress')
                : t('Add address', 'Lägg till adress')}
            </Button>
          </DialogFooter>
        </FormWrapper>
      </DialogContent>
    </Dialog>
  )
}
